import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  checkProfessionalAvailability,
  disconnectScreeningProfessional,
  getFiltersNewSearch,
  getProfessionalIsNotified,
  getPsyAliveList,
  getPsychologistAlreadyMigrated,
  getPsychologistDays,
  getPsychologistProfileV2,
  getPsychologistSchedule,
  getScreeningForwarded,
  getScreeningProfessionalList,
  getThemesPsyAliveProfessional,
  requestScheduleNotification,
  updateFilterPsyAlive,
  getProfessionalProfile,
  getAverageRatingScore,
  getProfessionalReviews,
  checkProfessionalSlots,
} from './services'
import {
  CheckProfessionalSlotsParams,
  GetPsyAliveListData,
  GetPsychologistProfileV2Params,
  PsychologistParams,
  UseCheckProfessionalAvailabilityParams,
  UseGetProfessionalIsNotifiedParams,
  UsePsyScheduleParams,
} from './types'
import { AxiosError } from 'axios'
import { toast } from '@conexasaude/conexa-hero'
import scheduleService from '@/modules/Attendance/submodules/Scheduled/scheduled-services'
import { useConexaQuery } from '@/hooks/useConexaQuery'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { CACHE_TIME_TWO_HOURS } from './constants'

type UsePsychologistDaysParams = {
  medicId: string
  month: string
  year: string
}

export const usePsychologistDays = (params: UsePsychologistDaysParams) => {
  const { medicId, month, year } = params

  const getMedicProfileQuery = useQuery(
    ['get-psychologist-days', medicId, month, year],
    {
      queryFn: async () => {
        return await getPsychologistDays(medicId, month, year)
      },
      refetchOnWindowFocus: false,
    },
  )

  return getMedicProfileQuery
}

export const useListPsychologists = (params: PsychologistParams) => {
  const handleError = (error: AxiosError) => {
    toast({
      message:
        error?.response?.data?.msg ??
        'Ocorreu um erro ao buscar os dados dos psicólogos, tente novamente.',
      variant: 'error',
    })
  }

  return useInfiniteQuery(
    ['get-psychologist-list'],
    async ({ pageParam = 1 }) =>
      await updateFilterPsyAlive({
        idPatient: params?.idPatient,
        ...params,
        page: pageParam,
      }),
    {
      getNextPageParam: (lastPage, pages) => {
        if (lastPage?.items && lastPage?.items?.length === 1) return
        if (lastPage?.items && lastPage?.items?.length > 0)
          return pages.length + 1
      },
      refetchOnWindowFocus: false,
      retry: false,
      onError: handleError,
    },
  )
}

export const usePsychologistThemes = (
  idPsychologist: number,
  enabled = true,
) => {
  const getPsychologistThemes = useQuery(
    ['get-psychologist-themes', idPsychologist],
    {
      queryFn: async () => {
        return await getThemesPsyAliveProfessional(idPsychologist)
      },
      enabled,
      refetchOnWindowFocus: false,
    },
  )

  return getPsychologistThemes
}

export const useGetPsychologistDetails = (id?: number, enabled = true) => {
  const query = useQuery(['get-psychologist-details', id], {
    queryFn: async () => {
      return await scheduleService.getMedicProfile(String(id))
    },
    enabled,
    refetchOnWindowFocus: false,
    cacheTime: 0,
    staleTime: 0,
  })

  return query
}

export const useScreeningProfessionalList = (patientId: number) => {
  const handleError = (error: AxiosError) => {
    toast({
      message:
        error?.response?.data?.msg ??
        'Ocorreu um erro ao buscar os dados dos psicólogos, tente novamente.',
      variant: 'error',
    })
  }

  return useInfiniteQuery({
    queryKey: ['screening-professional-list'],
    queryFn: ({ pageParam = 1 }) =>
      getScreeningProfessionalList(patientId, pageParam),
    getNextPageParam: (lastPage, pages) => {
      const total = lastPage?.total
      const allItems = pages?.map(item => item?.items)?.flat()

      if (total > allItems?.length) {
        return pages?.length + 1
      }
    },
    enabled: Boolean(patientId),
    retry: 0,
    refetchOnWindowFocus: false,
    onError: handleError,
  })
}

export const useDisconnectScreeningProfessional = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (patientId: number) =>
      disconnectScreeningProfessional(patientId),
    onSuccess: () => {
      queryClient.invalidateQueries(['screening-professional-list'])
      queryClient.invalidateQueries(['get-screening-forwarded'])
    },
  })
}

export const useGetScreeningForwarded = (patientId: number) => {
  const { clinic } = useAuthContext()

  const handleError = (error: AxiosError) => {
    toast({
      message: error?.response?.data?.msg,
      variant: 'error',
    })
  }

  return useConexaQuery({
    queryKey: [
      'get-screening-forwarded',
      `get-screening-forwarded-${patientId}`,
    ],
    service: () => getScreeningForwarded(patientId),
    config: {
      enabled:
        Boolean(patientId) &&
        clinic?.flag?.ClinicaUnificadaPV &&
        clinic?.flag?.HabilitarFluxoTriagem,
      retry: 0,
      refetchOnWindowFocus: false,
      onError: handleError,
    },
  })
}

export const useGetPsyList = (
  params: GetPsyAliveListData,
  isPsychologistAlreadyMigrated = true,
) => {
  return useInfiniteQuery(
    ['get-psychologist-list-v2', { ...params }],
    async ({ pageParam = 1 }) => {
      return await getPsyAliveList(
        {
          ...params,
          page: pageParam,
        },
        isPsychologistAlreadyMigrated,
      )
    },
    {
      getNextPageParam: (lastPage, pages) => {
        const total = lastPage?.total
        const allItems = pages?.map(item => item?.items)?.flat()

        if (total > allItems?.length) {
          return pages?.length + 1
        }
      },
      refetchOnWindowFocus: false,
      retry: false,
    },
  )
}

export const usePsySchedule = (params: UsePsyScheduleParams) => {
  const { professionalId, startDate, endDate } = params

  return useQuery(
    ['get-psychologist-days-v2', professionalId, startDate, endDate],
    {
      queryFn: async () => {
        return await getPsychologistSchedule(params)
      },
      enabled:
        Boolean(professionalId) && Boolean(startDate) && Boolean(endDate),
      refetchOnWindowFocus: false,
      retry: 1,
    },
  )
}

export const useFiltersNewSearch = () => {
  return useQuery(['filters-new-search'], {
    queryFn: async () => {
      return await getFiltersNewSearch()
    },
    refetchOnWindowFocus: false,
    retry: 1,
    cacheTime: CACHE_TIME_TWO_HOURS,
  })
}

export const usePsychologistAlreadyMigrated = () => {
  return useQuery(['show-new-search'], {
    queryFn: async () => {
      return await getPsychologistAlreadyMigrated('PSICOLOGO')
    },
    refetchOnWindowFocus: false,
    retry: 1,
    refetchOnMount: false,
    cacheTime: CACHE_TIME_TWO_HOURS,
  })
}

export const usePsychologistProfileV2 = (
  params: GetPsychologistProfileV2Params,
) => {
  return useQuery(
    ['psychologist-profile-v2-profile', params.clinicId, params.professionalId],
    {
      queryFn: async () => {
        return await getPsychologistProfileV2(params)
      },
      refetchOnWindowFocus: false,
      retry: 1,
      refetchOnMount: false,
      cacheTime: CACHE_TIME_TWO_HOURS,
    },
  )
}

export const useGetProfessionalIsNotified = ({
  professionalId,
  enabled,
}: UseGetProfessionalIsNotifiedParams) => {
  return useConexaQuery({
    queryKey: ['get-professional-is-notified', professionalId],
    service: () => getProfessionalIsNotified(professionalId),
    config: {
      enabled: enabled,
      refetchOnWindowFocus: false,
      retry: 1,
      refetchOnMount: false,
    },
  })
}

export const useCheckProfessionalAvailability = ({
  professionalId,
  enabled,
}: UseCheckProfessionalAvailabilityParams) => {
  return useQuery({
    queryKey: ['check-professional-availability', professionalId],
    queryFn: () => checkProfessionalAvailability(professionalId),
    enabled: enabled,
    refetchOnWindowFocus: false,
    retry: 1,
    refetchOnMount: false,
  })
}

export const useRequestScheduleNotification = () => {
  return useMutation({
    mutationFn: (professionalId: number) =>
      requestScheduleNotification(professionalId),
    onSuccess: () => {
      toast({
        message: 'Te avisaremos assim que abrirem novos horários!',
      })
    },
    onError: (error: AxiosError) => {
      toast({
        message:
          error?.response?.data?.msg ??
          'Ocorreu um erro ao solicitar a notificação de novos horários.',
        variant: 'error',
      })
    },
  })
}
export const useProfessionalProfile = (professionalId: string) => {
  return useQuery(['get-professional-profile-data', professionalId], {
    queryFn: async () => {
      return await getProfessionalProfile(professionalId)
    },
    enabled: Boolean(professionalId),
    refetchOnWindowFocus: false,
    retry: 1,
  })
}

export const useAverageRatingScore = (professionalId: string) => {
  return useQuery(['get-average-rating-score', professionalId], {
    queryFn: async () => {
      return await getAverageRatingScore(professionalId)
    },
    enabled: Boolean(professionalId),
    refetchOnWindowFocus: false,
    retry: 1,
  })
}

export const usePaginatedProfessionalReviews = (
  professionalId: string,
  size: number,
) => {
  return useInfiniteQuery(
    ['get-paginated-professional-reviews', professionalId],
    async ({ pageParam = 1 }) => {
      return await getProfessionalReviews(professionalId, pageParam, size)
    },
    {
      getNextPageParam: (lastPage, pages) => {
        const total = lastPage?.totalRecords
        const allItems = pages?.map(item => item?.items)?.flat()

        if (total > allItems?.length) {
          return pages?.length + 1
        }
      },
      enabled: Boolean(professionalId),
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  )
}

export const useCheckProfessionalSlots = () => {
  return useMutation({
    mutationFn: async ({
      slotId,
      professionalId,
      dateAndHour,
    }: CheckProfessionalSlotsParams) => {
      return await checkProfessionalSlots(slotId, professionalId, dateAndHour)
    },
  })
}
